import {
    AuthenticationPage,
    ContactDetailsPage,
    EmailVerificationPage,
    NotFoundPage,
    PasswordChangePage,
    PasswordRecoveryPage,
    PasswordResetPage,
    ProfileSettingsPage,
    YourListingsPage,
    UpdateCardPage,
    PaymentHistoryPage,
    LandscapePage,
    NewActionPage,
    ActionPage,
    NewProfilePage,
} from './containers';
//import { Personas } from './newContainers';
// routeConfiguration needs to initialize containers first
// Otherwise, components will import form container eventually and
// at that point css bundling / imports will happen in wrong order.
import { NamedRedirect } from './components';
//import BusinessPage from './newContainers/Partners/Partners';
//import GuardiansPageOld from './newContainers/Guardians';
import ProfileCheck from './newComponents/ProfileCheck/ProfileCheck';
import UserRoleCheck from './newComponents/UserCheck/userRoleCheck';
import SubdomainRouteRenderer from './components/SubdomainRouteRenderer';
import LeafLoader from './newComponents/LeafLoader/LeafLoader';

import React, { lazy, Suspense } from 'react';
import { Redirect } from 'react-router-dom';

// PERF: lazyLoad helper — wraps React.lazy() with Suspense + LeafLoader fallback.
// All newPages imports use this pattern so each page becomes its own JS chunk.
// Only the current page's chunk is downloaded when a user navigates to it.
// To revert a specific page to eager loading, replace:
//   const X = lazyLoad(() => import('./path/to/X'))
// with:
//   import X from './path/to/X'
// and remove it from the lazy section below.
const lazyLoad = importFn => {
    const LazyComponent = lazy(importFn);
    return props => (
        <Suspense fallback={<LeafLoader message="" />}>
            <LazyComponent {...props} />
        </Suspense>
    );
};

// ─── Lazy loaded pages ────────────────────────────────────────────────────
// Landing & Static pages
const NewLandingPage = lazyLoad(() => import('./newPages/NewLandingPage/NewLandingPage'));
const AboutUsPage = lazyLoad(() => import('./newPages/AboutUs/AboutUsPage.js'));
const FaqsPage = lazyLoad(() => import('./newPages/Faqs/FaqsPage.js'));
const PrivacyPolicyPage = lazyLoad(() => import('./newPages/PrivacyPolicy/PrivacyPolicyPage.js'));
const TermsOfServicePage = lazyLoad(() =>
    import('./newPages/TermsOfService/TermsOfServicePage.js')
);
const CookiePolicyPage = lazyLoad(() => import('./newPages/CookiePolicy/CookiePolicyPage.js'));
const SiteMapPage = lazyLoad(() => import('./newPages/SiteMap/index.js'));
const TeamUpPage = lazyLoad(() => import('./newPages/TeamUp/index.js'));
const PartnersPage = lazyLoad(() => import('./newPages/Partners/PartnersPage.js'));
const PricingPage = lazyLoad(() => import('./newPages/Partners/PricingPage/PricingPage.js'));
const FriendsPage = lazyLoad(() => import('./newPages/Friends/FriendsPage.js'));
const GuardiansPage = lazyLoad(() => import('./newPages/Guardians/GuardiansPage.js'));
const PublicProfilePage = lazyLoad(() => import('./newPages/PublicProfile/PublicProfilePage.js'));
const SummaryPage = lazyLoad(() => import('./newPages/SummaryPage/summaryPage.js'));
const FootprintPage = lazyLoad(() => import('./newPages/FootprintPage/footprintPage.js'));
const NaturePage = lazyLoad(() => import('./newPages/NatureStewardship/natureStewardship.js'));
const Roadmap = lazyLoad(() => import('./newPages/Roadmap/RoadmapPage.js'));
const OrganizationSettings = lazyLoad(() => import('./newPages/SettingsPage/ProfilePage.js'));
const DataInput = lazyLoad(() => import('./newPages/DataInput/PaymentPage.js'));

// Landscapes
const LandscapeList = lazyLoad(() => import('./newPages/Landscapes/LandscapeList/index.js'));
const LandscapeDetail = lazyLoad(() => import('./newPages/Landscapes/LandscapeDetail'));

// Blogs
const BlogList = lazyLoad(() => import('./newPages/Blogs').then(m => ({ default: m.BlogList })));
const BlogDetailPage = lazyLoad(() =>
    import('./newPages/Blogs').then(m => ({ default: m.BlogDetailPage }))
);
const ChangelogPage = lazyLoad(() => import('./newPages/Changelogs/ChangelogPage'));
const KnowledgeHubPage = lazyLoad(() => import('./newPages/KnowledgeHub/KnowledgeHubPage.js'));

// Auth & Signup
// const SignupPage = lazyLoad(() => import('./newPages/Signup/SignupPage.js')); // kept for reference — new flow uses SignupShell
const SignupShell = lazyLoad(() => import('./newPages/Signup/SignupShell.js'));
const LoginPage = lazyLoad(() => import('./newPages/Login/LoginPage.js'));

// Subscription flow
const Signup = lazyLoad(() => import('./newPages/MultiStepSubscription/Signup'));
const ConfirmPlan = lazyLoad(() => import('./newPages/MultiStepSubscription/ConfirmPlan.js'));
const OneTimePayment = lazyLoad(() => import('./newPages/MultiStepSubscription/OneTimePayment.js'));
const SelectLandscape = lazyLoad(() =>
    import('./newPages/MultiStepSubscription/SelectLandscape.js')
);
const MakePayment = lazyLoad(() => import('./newPages/MultiStepSubscription/MakePayment.js'));
// import FinalStep from './newPages/MultiStepSubscription/PaymentSuccess.js'; // commented out in original

// Guardian signup flow
const GuardianRole = lazyLoad(() => import('./newPages/Guardians/MultiStepSignup/GuardianRole.js'));
const GuardianSignup = lazyLoad(() =>
    import('./newPages/Guardians/MultiStepSignup/GuardianSignup/GuardianSignup.js')
);
const SelectCountry = lazyLoad(() =>
    import('./newPages/Guardians/MultiStepSignup/SelectCountry.js')
);
const DocumentWorkFlow = lazyLoad(() =>
    import('./newPages/Guardians/MultiStepSignup/DocuSign/DocumentWorkFlow.js')
);

// MyNature (Partner - individual) pages
const MyNatureHomePage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages').then(m => ({ default: m.MyNatureHomePage }))
);
const MyNatureFootprintPage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages').then(m => ({
        default: m.MyNatureFootprintPage,
    }))
);
const MyNatureImpactPage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages').then(m => ({ default: m.MyNatureImpactPage }))
);
const MyNatureLandscapePage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages').then(m => ({
        default: m.MyNatureLandscapePage,
    }))
);
const MyNatureProfileAndAccountPage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages').then(m => ({
        default: m.MyNatureProfileAndAccountPage,
    }))
);
const ActivateAccountPage = lazyLoad(() =>
    import('./newPages/myNaturePage/sections/pages/ActivateAccount/ActivateAccount')
);

// OurNature (Partner - org) pages
const OurNatureHomePage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages').then(m => ({ default: m.OurNatureHomePage }))
);
const OurNatureFootprintPage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages').then(m => ({
        default: m.OurNatureFootprintPage,
    }))
);
const OurNatureImpactPage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages').then(m => ({
        default: m.OurNatureImpactPage,
    }))
);
const OurNatureLandscapePage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages').then(m => ({
        default: m.OurNatureLandscapePage,
    }))
);
const OurNatureProfileAndAccountPage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages').then(m => ({
        default: m.OurNatureProfileAndAccountPage,
    }))
);
const ActivatePartnerRolePage = lazyLoad(() =>
    import('./newPages/ourNaturePage/sections/pages/ActivateRoles/ActivateRoles')
);

// Guardian profile pages
const GuardianHomePage = lazyLoad(() =>
    import('./newPages/guardianNaturePage').then(m => ({ default: m.GuardianHomePage }))
);
const GuardianProfileAndAccountPage = lazyLoad(() =>
    import('./newPages/guardianNaturePage').then(m => ({
        default: m.GuardianProfileAndAccountPage,
    }))
);
const GuardianLandAndLandscapePage = lazyLoad(() =>
    import('./newPages/guardianNaturePage').then(m => ({
        default: m.GuardianLandAndLandscapePage,
    }))
);
const GuardianDocumentsPage = lazyLoad(() =>
    import('./newPages/guardianNaturePage').then(m => ({ default: m.GuardianDocumentsPage }))
);
const GuardianIncomePage = lazyLoad(() =>
    import('./newPages/guardianNaturePage').then(m => ({ default: m.GuardianIncomePage }))
);

// Organisation calculator (authenticated)
const OrganisationDetailsPage = lazyLoad(() => import('./newPages/organisationCalculator'));
const DirectEmissionPage = lazyLoad(() =>
    import('./newPages/organisationCalculator/DirectEmissionPage/DirectEmission.js')
);
const ElectricityEmissionPage = lazyLoad(() =>
    import('./newPages/organisationCalculator/ElectricityPage/electricityEmission.js')
);
const IndirectEmissionPage = lazyLoad(() =>
    import('./newPages/organisationCalculator/IndirectEmissionPage/IndirectEmission.js')
);
const ResultPage = lazyLoad(() =>
    import('./newPages/organisationCalculator/ResultPage/resultPage.js')
);

// 🌿 Public Calculator Routes — lazy loaded, no auth required
const PublicOrganisationDetailsPage = lazyLoad(() =>
    import('./newPages/PublicOrganisationCalculator/orgnisationDetails/PublicDetails.js')
);
const PublicDirectEmissionPage = lazyLoad(() =>
    import('./newPages/PublicOrganisationCalculator/DirectEmissionPage/DirectEmission.js')
);
const PublicElectricityEmissionPage = lazyLoad(() =>
    import('./newPages/PublicOrganisationCalculator/ElectricityPage/electricityEmission.js')
);
const PublicIndirectEmissionPage = lazyLoad(() =>
    import('./newPages/PublicOrganisationCalculator/IndirectEmissionPage/IndirectEmission.js')
);
const PublicResultPage = lazyLoad(() =>
    import('./newPages/PublicOrganisationCalculator/ResultPage/resultPage.js')
);

// Misc
const PersonalFootprintCalculatorPage = lazyLoad(() =>
    import('./newPages/PersonalFootprintCalculatorPage/PersonalFootprintCalculatorPage.js')
);

export const ACCOUNT_SETTINGS_PAGES = [
    'ContactDetailsPage',
    'PasswordChangePage',
    'PayoutPreferencesPage',
];

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

const RedirectToLandingPage = () => <NamedRedirect name="LandingPage" />;
const handlePy = res => console.log(res);
// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.

const routeConfiguration = () => {
    return [
        {
            path: '/',
            name: 'LandingPage',
            component: props => <SubdomainRouteRenderer {...props} />,
        },
        {
            path: '/about',
            name: 'AboutUsPage',
            component: props => <AboutUsPage {...props} />,
        },
        {
            path: '/summary',
            name: 'SummaryPage',
            component: props => <SummaryPage {...props} />,
        },
        {
            path: '/pricing',
            name: 'PricingPage',
            component: props => <PricingPage {...props} />,
        },
        {
            path: '/publicprofile',
            name: 'Public Profile',
            component: props => <PublicProfilePage {...props} />,
        },
        {
            path: '/footprint',
            name: 'FootprintManagement',
            component: props => <FootprintPage {...props} />,
        },
        {
            path: '/nature',
            name: 'NatureManagement',
            component: props => <NaturePage {...props} />,
        },
        {
            path: '/roadmap',
            name: 'Baseline and Roadmap',
            component: props => <Roadmap {...props} />,
        },
        {
            path: '/data-input',
            name: 'Data Input',
            component: props => <DataInput {...props} />,
        },
        {
            path: '/organisation-info',
            name: 'Organisation Info',
            component: props => <OrganizationSettings {...props} />,
        },
        {
            path: '/friends-profile/home',
            name: 'friends-profile/home',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <MyNatureHomePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/friends-profile/footprint',
            name: 'friends-profile/footprint',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <MyNatureFootprintPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/friends-profile/impact',
            name: 'friends-profile/impact',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <MyNatureImpactPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/friends-profile/landscape',
            name: 'friends-profile/landscape',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <MyNatureLandscapePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/friends-profile/profileandaccount',
            name: 'friends-profile/profileandaccount',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <MyNatureProfileAndAccountPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/friends-profile/activateaccount',
            name: 'friends-profile/activateaccount',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="friend">
                    <ActivateAccountPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/home',
            name: 'partner-profile/home',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <OurNatureHomePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/footprint',
            name: 'partner-profile/footprint',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <OurNatureFootprintPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/impact',
            name: 'partner-profile/impact',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <OurNatureImpactPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/landscape',
            name: 'partner-profile/landscape',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <OurNatureLandscapePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/profileandaccount',
            name: 'partner-profile/profileandaccount',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <OurNatureProfileAndAccountPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/partner-profile/activateroles',
            name: 'partner-profile/activateroles',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="partner">
                    <ActivatePartnerRolePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/guardian-profile/profileandaccount',
            name: 'guardian-profile/profileandaccount',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="guardian">
                    <GuardianProfileAndAccountPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/guardian-profile/income',
            name: 'guardian-profile/income',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="guardian">
                    <GuardianIncomePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/guardian-profile/home',
            name: 'guardian-profile/home',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="guardian">
                    <GuardianHomePage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/guardian-profile/documents',
            name: 'guardian-profile/documents',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="guardian">
                    <GuardianDocumentsPage {...props} />
                </UserRoleCheck>
            ),
        },
        {
            path: '/guardian-profile/landandlandscape',
            name: 'guardian-profile/landandlandscape',
            auth: true,
            authPage: 'LoginPage',
            component: props => (
                <UserRoleCheck accpetedRole="guardian">
                    <GuardianLandAndLandscapePage {...props} />
                </UserRoleCheck>
            ),
        },
        // {
        //     path: '/land-managers-old',
        //     name: 'GuardiansPage-old',
        //     component: props => <GuardiansPageOld {...props} />,
        // },
        {
            path: '/land-managers',
            name: 'GuardiansPage',
            component: props => <GuardiansPage {...props} />,
        },
        {
            path: '/landscapes',
            name: 'Paisajes',
            component: props => <LandscapeList {...props} />,
        },
        {
            path: '/people',
            name: 'Personas',
            component: props => <FriendsPage {...props} />,
        },
        {
            path: '/organizations',
            name: 'BusinessPage',
            component: props => <PartnersPage {...props} />,
        },
        // {
        //     path: '/friends',
        //     name: 'Friends',
        //     component: props => <Personas {...props} />,
        // },

        // {
        //     path: '/landscapes-old/:slug',
        //     name: 'ListingPage',
        //     component: props => <LandscapePage {...props} />,
        // },
        {
            path: '/landscapes-old/:slug',
            name: 'LandscapesOldRedirect',
            component: ({ match }) => <Redirect to={`/landscapes/${match.params.slug}`} />,
        },
        {
            path: '/landscapes/:slug',
            name: 'ListingPage',
            component: props => <LandscapeDetail {...props} />,
        },
        {
            path: '/action/:slug',
            name: 'ActionPage',
            component: props => <ActionPage {...props} />,
        },
        {
            path: '/new-action',
            name: 'NewActionPage',
            auth: true,
            component: props => <NewActionPage {...props} />,
        },
        {
            path: '/user/:slug',
            name: 'ProfilePage',
            component: props => <NewProfilePage {...props} />,
            // loadData: ProfilePage.loadData,
        },
        {
            path: '/profile-settings/myNature',
            name: 'MyNatureProfileSettingsPage',
            auth: true,
            authPage: 'LandingPage',
            component: props => <MyNatureProfileAndAccountPage {...props} />,
        },
        {
            path: '/profile-settings/ourNature',
            name: 'OurNatureProfileSettingsPage',
            auth: true,
            authPage: 'LandingPage',
            component: props => <OurNatureProfileAndAccountPage {...props} />,
        },
        // {
        //     path: '/login',
        //     name: 'LoginPage',
        //     component: props => <AuthenticationPage {...props} tab="login" />,
        // },
        // {
        //     path: '/signup',
        //     name: 'SignupPage',
        //     component: props => <AuthenticationPage {...props} tab="signup" />,
        // },
        {
            path: '/recover-password',
            name: 'PasswordRecoveryPage',
            component: props => <PasswordRecoveryPage {...props} />,
        },
        {
            path: '/account',
            name: 'AccountSettingsPage',
            auth: true,
            authPage: 'LoginPage',
            component: () => <NamedRedirect name="ContactDetailsPage" />,
        },
        {
            path: '/account/contact-details',
            name: 'ContactDetailsPage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <ContactDetailsPage {...props} />,
            loadData: ContactDetailsPage.loadData,
        },
        {
            path: '/account/tus-listings',
            name: 'YourListingsPage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <YourListingsPage {...props} />,
            loadData: YourListingsPage.loadData,
        },
        {
            path: '/account/change-password',
            name: 'PasswordChangePage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <PasswordChangePage {...props} />,
        },
        {
            path: '/account/update-card',
            name: 'UpdateCardPage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <UpdateCardPage {...props} />,
        },
        {
            path: '/terms-of-service',
            name: 'TermsOfServicePage',
            component: props => <TermsOfServicePage {...props} />,
        },
        {
            path: '/privacy-policy',
            name: 'PrivacyPolicyPage',
            component: props => <PrivacyPolicyPage {...props} />,
        },
        {
            path: '/cookie-policy',
            name: 'CookiePolicyPage',
            component: props => <CookiePolicyPage {...props} />,
        },
        {
            path: '/notfound',
            name: 'NotFoundPage',
            component: props => <NotFoundPage {...props} />,
        },
        // Do not change this path!
        //
        // The API expects that the application implements /reset-password endpoint
        {
            path: '/reset-password',
            name: 'PasswordResetPage',
            component: props => <PasswordResetPage {...props} />,
        },
        // Do not change this path!
        //
        // The API expects that the application implements /verify-email endpoint
        {
            path: '/verify-email',
            name: 'EmailVerificationPage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <EmailVerificationPage {...props} />,
            loadData: EmailVerificationPage.loadData,
        },
        {
            path: '/faq',
            name: 'FaqsPage',
            component: props => <FaqsPage {...props} />,
        },
        {
            path: '/account/payment-history',
            name: 'PaymentHistoryPage',
            auth: true,
            authPage: 'LoginPage',
            component: props => <PaymentHistoryPage {...props} />,
        },
        {
            path: '/people/personal-footprint-calculator',
            name: 'PersonalFootprintCalculatorPage',
            component: props => <PersonalFootprintCalculatorPage {...props} />,
        },
        {
            path: '/organisation/calculator/direct-emission',
            name: '/organisation/calculator/direct-emission',
            auth: true,
            organisationStateCheck: true,
            organisationRedirectPath: '/organisation/calculator',
            authPage: 'LoginPage',
            component: props => <DirectEmissionPage {...props} />,
        },
        {
            path: '/organisation/calculator/electricity-emission',
            name: '/organisation/calculator/electricity-emission',
            auth: true,
            organisationStateCheck: true,
            organisationRedirectPath: '/organisation/calculator',
            authPage: 'LoginPage',
            component: props => <ElectricityEmissionPage {...props} />,
        },
        {
            path: '/organisation/calculator/indirect-emission',
            name: '/organisation/calculator/indirect-emission',
            auth: true,
            organisationStateCheck: true,
            organisationRedirectPath: '/organisation/calculator',
            authPage: 'LoginPage',
            component: props => <IndirectEmissionPage {...props} />,
        },
        {
            path: '/organisation/calculator/result',
            name: '/organisation/calculator/result',
            auth: true,
            organisationStateCheck: true,
            organisationRedirectPath: '/organisation/calculator',
            authPage: 'LoginPage',
            component: props => <ResultPage {...props} />,
        },
        {
            path: '/organisation/calculator',
            name: '/organisation/calculator',
            auth: true,
            authPage: 'LoginPage',
            component: props => <OrganisationDetailsPage {...props} />,
        },
        // 🌿 Public Calculator Routes
        {
            path: '/calculator',
            name: 'PublicOrganisationDetailsPage',
            auth: false,
            component: props => <PublicOrganisationDetailsPage {...props} />,
        },
        {
            path: '/calculator/direct-emission',
            name: 'PublicDirectEmissionPage',
            auth: false,
            component: props => <PublicDirectEmissionPage {...props} />,
        },
        {
            path: '/calculator/electricity-emission',
            name: 'PublicElectricityEmissionPage',
            auth: false,
            component: props => <PublicElectricityEmissionPage {...props} />,
        },
        {
            path: '/calculator/indirect-emission',
            name: 'PublicIndirectEmissionPage',
            auth: false,
            component: props => <PublicIndirectEmissionPage {...props} />,
        },
        {
            path: '/calculator/result',
            name: 'PublicResultPage',
            auth: false,
            component: props => <PublicResultPage {...props} />,
        },
        {
            path: '/profile-check',
            name: 'ProfileCheck',
            auth: true,
            authPage: 'LoginPage',
            component: props => <ProfileCheck {...props} />,
        },
        {
            path: '/blog/:newsSlug',
            name: 'BlogDetailPage',
            component: props => <BlogDetailPage {...props} />,
        },
        {
            path: '/blog',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/blog/search/:newsKeywords',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/blog/category/:newsCategory',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/blog/landscape/:newsLandscape',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/blog/tag/:newsTag',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/blog/author/:newsWriter',
            name: 'BlogList',
            component: props => <BlogList {...props} />,
        },
        {
            path: '/changelog',
            name: 'ChangelogPage',
            auth: false,
            component: props => <ChangelogPage {...props} />,
        },
        {
            path: '/knowledge-hub',
            name: 'KnowledgeHubPage',
            auth: false,
            component: props => <KnowledgeHubPage {...props} />,
        },
        // this will be removed later when /news is implemented for the news section on wordpress //
        {
            path: '/news/:newsSlug',
            component: match => <Redirect to={`/blog/${match.params.newsSlug}`} />,
        },
        {
            path: '/subscription/signup',
            name: 'Subscription',
            component: props => <Signup {...props} />,
        },
        {
            path: '/subscription/confirm-plan',
            name: 'Plan',
            auth: true,
            component: props => <ConfirmPlan {...props} />,
        },
        {
            path: '/subscription/onetime-payment',
            name: 'Plan',
            auth: true,
            component: props => <OneTimePayment {...props} />,
        },
        {
            path: '/subscription/select-landscape',
            name: 'Plan',
            auth: true,
            component: props => <SelectLandscape {...props} />,
        },
        {
            path: '/subscription/payment',
            name: 'Plan',
            auth: true,
            authPage: 'LoginPage',
            component: props => <MakePayment {...props} />,
        },
        {
            path: '/land-managers/role',
            name: 'GuardianRole',
            component: props => <GuardianRole {...props} />,
        },
        {
            path: '/land-managers/signup',
            name: 'GuardianSignup',
            component: props => <GuardianSignup {...props} />,
        },
        {
            path: '/land-managers/select-country',
            name: 'SelectCountry',
            auth: true,
            authPage: 'GuardianSignup',
            component: props => <SelectCountry {...props} />,
        },
        {
            path: '/land-managers/sign-document',
            name: 'DocumentWorkFlow',
            auth: true,
            authPage: 'GuardianSignup',
            component: props => <DocumentWorkFlow {...props} />,
        },
        {
            path: '/guardians-allies',
            name: 'GuardiansAlliesRedirect',
            component: () => <Redirect to="/land-managers" />,
        },
        {
            path: '/signup',
            name: 'SignupRedirect',
            component: () => <Redirect to="/signup/start" />,
        },
        //new signup flow routes
        {
            path: '/signup/:rest(.*)',
            name: 'SignupCatchAll',
            component: props => <SignupShell {...props} />,
        },
        //end new signup flow routes
        // {
        //     path: '/signup',
        //     name: 'SignupPage',
        //     component: props => <SignupPage {...props} />,
        // },
        {
            path: '/login',
            name: 'LoginPage',
            component: props => <LoginPage {...props} />,
        },
        {
            path: '/team-up',
            name: 'TeamUpPage',
            component: props => <TeamUpPage {...props} />,
        },
        {
            path: '/sitemap',
            name: 'SiteMap',
            component: props => <SiteMapPage {...props} />,
        },
    ];
};

export default routeConfiguration;
